import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError } from "@/api/client";
import { Plus, Globe, Edit, Trash2, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const COLOR_PRESETS = ["#41B5E6", "#0F172A", "#7C3AED", "#059669", "#DC2626", "#EA580C", "#0EA5E9", "#DB2777"];

export default function SpacesListPage() {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/spaces").then((r) => setSpaces(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onDelete = async (id) => {
    if (!confirm("Delete this space and ALL its content? This can't be undone.")) return;
    try {
      await api.delete(`/spaces/${id}`);
      toast.success("Space deleted");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <div className="p-6 sm:p-10 max-w-7xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 font-bold">Workspaces</div>
          <h1 className="text-3xl font-black tracking-tight">Your spaces</h1>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="new-space-btn"
          className="bg-[#41B5E6] hover:bg-[#2A9BCC] text-white px-4 py-2.5 rounded-sm font-semibold text-sm flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> New Space
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : spaces.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-sm p-12 text-center">
          <div className="text-zinc-500 mb-4">No spaces yet. Create your first help center.</div>
          <button onClick={() => setShowCreate(true)} className="bg-[#41B5E6] hover:bg-[#2A9BCC] text-white px-4 py-2 rounded-sm font-semibold text-sm">
            Create Space
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="spaces-grid">
          {spaces.map((s) => (
            <div key={s.id} className="bg-white border border-zinc-200 rounded-sm p-5 hover:border-zinc-400 transition-colors group"
              data-testid={`space-card-${s.slug}`}>
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-10 h-10 rounded-sm flex items-center justify-center text-white font-black"
                  style={{ background: s.branding?.primary_color || "#41B5E6" }}
                >
                  {s.name.charAt(0)}
                </div>
                <span
                  className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm ${
                    s.status === "published" ? "bg-emerald-100 text-emerald-800" :
                    s.status === "draft" ? "bg-zinc-100 text-zinc-700" :
                    "bg-amber-100 text-amber-800"
                  }`}
                >
                  {s.status}
                </span>
              </div>
              <h3 className="font-bold text-lg leading-tight mb-1">{s.name}</h3>
              <p className="text-sm text-zinc-500 mb-4 line-clamp-2">{s.description || "No description"}</p>
              <div className="flex gap-4 text-xs text-zinc-500 mb-4">
                <span>{s.stats?.total_articles || 0} articles</span>
                <span>{s.stats?.total_views || 0} views</span>
              </div>
              <div className="flex gap-2 pt-3 border-t border-zinc-100">
                <Link
                  to={`/spaces/${s.id}/content`}
                  data-testid={`edit-space-${s.slug}`}
                  className="flex-1 text-center px-3 py-2 text-xs font-bold uppercase tracking-wider bg-zinc-900 text-white hover:bg-zinc-700 rounded-sm transition-colors"
                >
                  <Edit className="w-3 h-3 inline mr-1" /> Edit
                </Link>
                {s.status === "published" && (
                  <Link
                    to={`/s/${s.slug}`}
                    target="_blank"
                    className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-zinc-300 hover:bg-zinc-50 rounded-sm transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                )}
                <button
                  onClick={() => onDelete(s.id)}
                  className="px-3 py-2 text-xs border border-zinc-300 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-600 rounded-sm transition-colors"
                  data-testid={`delete-space-${s.slug}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <CreateSpaceModal onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  );
}

function CreateSpaceModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [color, setColor] = useState(COLOR_PRESETS[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const computedSlug = slug || name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/spaces", { name, slug: computedSlug || name, primary_color: color });
      toast.success("Space created");
      onClose();
      onCreated();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-sm" onClick={(e) => e.stopPropagation()} data-testid="create-space-modal">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <h2 className="font-bold text-lg">New Space</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-zinc-500" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-bold mb-1.5">Name</label>
            <input required value={name} onChange={(e) => setName(e.target.value)}
              data-testid="new-space-name"
              placeholder="e.g. Customer Help Center"
              className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#41B5E6]" />
          </div>
          <div>
            <label className="block text-sm font-bold mb-1.5">Slug</label>
            <div className="flex items-center border border-zinc-300 rounded-sm overflow-hidden">
              <span className="px-3 py-2.5 text-xs text-zinc-500 bg-zinc-50 border-r border-zinc-300 select-none">/s/</span>
              <input value={slug} onChange={(e) => setSlug(e.target.value)}
                data-testid="new-space-slug"
                placeholder={computedSlug || "your-help-center"}
                className="flex-1 px-3 py-2.5 text-sm focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold mb-1.5">Theme color</label>
            <div className="flex gap-2 flex-wrap">
              {COLOR_PRESETS.map((c) => (
                <button key={c} type="button" onClick={() => setColor(c)}
                  className={`w-9 h-9 rounded-sm border-2 transition-all ${color === c ? "border-zinc-900 scale-110" : "border-transparent"}`}
                  style={{ background: c }} />
              ))}
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
                className="w-9 h-9 rounded-sm border border-zinc-300 cursor-pointer" />
            </div>
          </div>
          {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 px-3 py-2 rounded-sm">{error}</div>}
          <button type="submit" disabled={loading || !name}
            data-testid="create-space-submit"
            className="w-full bg-[#41B5E6] hover:bg-[#2A9BCC] text-white font-semibold py-2.5 rounded-sm disabled:opacity-50">
            {loading ? "Creating…" : "Create Space"}
          </button>
        </form>
      </div>
    </div>
  );
}
